import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Edit2, Users, UserCheck, Clock, FileText,
  FolderKanban, CheckCircle, AlertCircle, Calendar, ChevronRight,
  ClipboardList, Megaphone, Plus, Trash2, X, Send
} from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';
import RichTextEditor from '../components/RichTextEditor';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubProjectView = ({ user }) => {
  const { projectId, subProjectId } = useParams();
  const navigate = useNavigate();
  const [parentProject, setParentProject] = useState(null);
  const [subProject, setSubProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    team_head_ids: [],
    assigned_to: [],
    priority: 5,
    deadline: '',
    status: 'active'
  });
  const [allUsers, setAllUsers] = useState([]);

  // Noticeboard state
  const [noticeboardItems, setNoticeboardItems] = useState([]);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [noticeForm, setNoticeForm] = useState({
    title: '',
    content: '',
    priority: 'normal'
  });

  const fetchData = useCallback(async (abortSignal) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const config = abortSignal ? { headers, signal: abortSignal } : { headers };

      // Fetch parent project
      const projectRes = await axios.get(`${API_URL}/projects/${projectId}`, config);
      setParentProject(projectRes.data);

      // Fetch sub-projects to find the specific one
      const subProjectsRes = await axios.get(`${API_URL}/projects/${projectId}/subprojects`, config);
      const sp = subProjectsRes.data.find(s => s.id === subProjectId);
      if (sp) {
        setSubProject(sp);
        setEditForm({
          title: sp.title,
          description: sp.description || '',
          team_head_ids: sp.team_head_ids || [],
          assigned_to: sp.assigned_to || [],
          priority: sp.priority || 5,
          deadline: sp.deadline ? sp.deadline.split('T')[0] : '',
          status: sp.status || 'active'
        });
      }

      // Fetch tasks for this sub-project
      const tasksRes = await axios.get(`${API_URL}/tasks?subproject_id=${subProjectId}`, config);
      // API returns { assigned_to_me, assigned_by_me, common, counts } - combine all task arrays
      const taskData = tasksRes.data || {};
      const allTasks = [
        ...(taskData.assigned_to_me || []),
        ...(taskData.assigned_by_me || []),
        ...(taskData.common || [])
      ];
      // Remove duplicates based on task id
      const uniqueTasks = allTasks.filter((task, index, self) => 
        index === self.findIndex(t => t.id === task.id)
      );
      setTasks(uniqueTasks);

      // Fetch users
      const usersRes = await axios.get(`${API_URL}/users`, config);
      setAllUsers(usersRes.data || []);

      // Fetch noticeboard items for sub-project
      try {
        const noticeRes = await axios.get(`${API_URL}/subprojects/${subProjectId}/noticeboard`, config);
        setNoticeboardItems(noticeRes.data || []);
      } catch (e) {
        if (!axios.isCancel(e)) {
          setNoticeboardItems([]);
        }
      }

    } catch (error) {
      // Only show error if not aborted/cancelled
      if (!axios.isCancel(error)) {
        console.error('Failed to fetch sub-project:', error);
        toast.error('Failed to load sub-project');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, subProjectId]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchData(abortController.signal);
    
    return () => {
      abortController.abort();
    };
  }, [fetchData]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/projects/${projectId}/subprojects/${subProjectId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sub-project updated');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sub-project');
    }
  };

  // Noticeboard handlers
  const handleAddNotice = async (e) => {
    e.preventDefault();
    if (!noticeForm.title.trim() || !noticeForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (editingNotice) {
        await axios.put(`${API_URL}/subprojects/${subProjectId}/noticeboard/${editingNotice.id}`, noticeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Notice updated');
      } else {
        await axios.post(`${API_URL}/subprojects/${subProjectId}/noticeboard`, noticeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Notice added');
      }
      setShowNoticeModal(false);
      setEditingNotice(null);
      setNoticeForm({ title: '', content: '', priority: 'normal' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notice');
    }
  };

  const handleDeleteNotice = async (noticeId) => {
    if (!window.confirm('Delete this notice?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/subprojects/${subProjectId}/noticeboard/${noticeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete notice');
    }
  };

  const canManageNoticeboard = ['superadmin', 'manager', 'ceo'].includes(user?.role);
  const canEdit = ['superadmin', 'manager', 'ceo'].includes(user?.role);

  const cleanName = (name) => {
    if (!name) return 'Unknown';
    return name.replace(/\s+\d+$/, '').trim();
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'completed': return { bg: '#D1FAE5', color: '#059669', label: 'Completed' };
      case 'in_progress': return { bg: '#DBEAFE', color: '#2563EB', label: 'In Progress' };
      case 'on_hold': return { bg: '#FEF3C7', color: '#D97706', label: 'On Hold' };
      default: return { bg: '#E5E7EB', color: '#6B7280', label: 'Active' };
    }
  };

  const getPriorityLabel = (p) => {
    if (p <= 3) return { label: 'High', color: '#EF4444' };
    if (p <= 6) return { label: 'Medium', color: '#F59E0B' };
    return { label: 'Low', color: '#10B981' };
  };

  if (loading) {
    return (
      <div className="content-area" style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading sub-project...</p>
      </div>
    );
  }

  if (!subProject) {
    return (
      <div className="content-area" style={{ padding: '60px', textAlign: 'center' }}>
        <FolderKanban size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
        <h2>Sub-Project Not Found</h2>
        <button onClick={() => navigate(-1)} className="gradient-btn" style={{ marginTop: '16px' }}>
          Go Back
        </button>
      </div>
    );
  }

  const statusStyle = getStatusStyle(subProject.status);
  const priorityInfo = getPriorityLabel(subProject.priority);
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const progressPercent = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0;

  return (
    <div className="content-area" data-testid="subproject-view">
      {/* Breadcrumb & Back */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate(`/dashboard/projects/${projectId}`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          <ArrowLeft size={18} />
          Back to Project
        </button>
        {parentProject && (
          <span style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>
            {parentProject.name} / <strong style={{ color: 'var(--text-primary)' }}>{subProject.title}</strong>
          </span>
        )}
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '12px',
            background: `${parentProject?.color || '#7077FE'}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FolderKanban size={28} color={parentProject?.color || '#7077FE'} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
              {subProject.title}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
              <span style={{
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                background: statusStyle.bg,
                color: statusStyle.color
              }}>
                {statusStyle.label}
              </span>
              <span style={{
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                background: `${priorityInfo.color}20`,
                color: priorityInfo.color
              }}>
                {priorityInfo.label} Priority
              </span>
              {subProject.deadline && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  <Calendar size={14} />
                  Due: {new Date(subProject.deadline).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowEditModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              fontWeight: '500'
            }}
            data-testid="edit-subproject-btn"
          >
            <Edit2 size={16} /> Edit
          </button>
        )}
      </div>

      {/* Description Card */}
      {subProject.description && (
        <div className="card" style={{ padding: '20px', marginBottom: '24px', borderLeft: `4px solid ${parentProject?.color || '#7077FE'}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <FileText size={24} color={parentProject?.color || '#7077FE'} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Description
              </h3>
              <div
                style={{ color: 'var(--text-primary)', fontSize: '15px', lineHeight: '1.7' }}
                dangerouslySetInnerHTML={{ __html: subProject.description }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Progress Overview */}
      <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
          Progress Overview
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '20px' }}>
          <div style={{ textAlign: 'center', padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#7077FE' }}>{tasks.length}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>Total Tasks</div>
          </div>
          <div style={{ textAlign: 'center', padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#10B981' }}>{completedTasks}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>Completed</div>
          </div>
          <div style={{ textAlign: 'center', padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#F59E0B' }}>{tasks.length - completedTasks}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>Pending</div>
          </div>
          <div style={{ textAlign: 'center', padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: parentProject?.color || '#7077FE' }}>{progressPercent}%</div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>Progress</div>
          </div>
        </div>
        {/* Progress Bar */}
        <div style={{ marginTop: '20px' }}>
          <div style={{ height: '8px', background: 'var(--bg-secondary)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${progressPercent}%`,
              background: `linear-gradient(90deg, ${parentProject?.color || '#7077FE'}, #10B981)`,
              borderRadius: '4px',
              transition: 'width 0.3s'
            }} />
          </div>
        </div>
      </div>

      {/* Noticeboard Section */}
      <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Megaphone size={20} color="#F59E0B" /> Sub-Project Noticeboard
          </h3>
          {canManageNoticeboard && (
            <button
              onClick={() => { setEditingNotice(null); setNoticeForm({ title: '', content: '', priority: 'normal' }); setShowNoticeModal(true); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                background: '#FEF3C7',
                border: '1px solid #FCD34D',
                borderRadius: '8px',
                color: '#D97706',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '13px'
              }}
              data-testid="add-subproject-notice-btn"
            >
              <Plus size={16} /> Add Notice
            </button>
          )}
        </div>

        {noticeboardItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-tertiary)' }}>
            <Megaphone size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p style={{ margin: 0 }}>No notices posted yet</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {noticeboardItems.map(notice => {
              const priorityColors = {
                urgent: { bg: '#FEE2E2', border: '#EF4444', text: '#DC2626' },
                important: { bg: '#FEF3C7', border: '#F59E0B', text: '#D97706' },
                normal: { bg: 'var(--bg-secondary)', border: 'var(--border-color)', text: 'var(--text-primary)' }
              };
              const pStyle = priorityColors[notice.priority] || priorityColors.normal;

              return (
                <div
                  key={notice.id}
                  style={{
                    padding: '16px',
                    background: pStyle.bg,
                    border: `1px solid ${pStyle.border}`,
                    borderRadius: '10px',
                    borderLeft: notice.priority === 'urgent' ? '4px solid #EF4444' : notice.priority === 'important' ? '4px solid #F59E0B' : '4px solid var(--border-color)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: pStyle.text }}>{notice.title}</h4>
                        {notice.priority !== 'normal' && (
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            background: notice.priority === 'urgent' ? '#EF4444' : '#F59E0B',
                            color: 'white'
                          }}>
                            {notice.priority}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: notice.content }} />
                      <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Clock size={12} />
                        {new Date(notice.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {notice.created_by_name && <span>• by {notice.created_by_name}</span>}
                      </div>
                    </div>
                    {canManageNoticeboard && (
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button
                          onClick={() => { setEditingNotice(notice); setNoticeForm({ title: notice.title, content: notice.content, priority: notice.priority || 'normal' }); setShowNoticeModal(true); }}
                          style={{ padding: '6px', background: 'var(--bg-primary)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-secondary)' }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteNotice(notice.id)}
                          style={{ padding: '6px', background: 'rgba(239, 68, 68, 0.1)', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#EF4444' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        {/* Tasks Section */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ClipboardList size={20} color="#7077FE" /> Tasks ({tasks.length})
            </h3>
            <button
              onClick={() => navigate(`/dashboard/tasks/new?subproject_id=${subProjectId}&project_id=${projectId}`)}
              className="gradient-btn"
              style={{ padding: '8px 16px', fontSize: '13px' }}
            >
              <Plus size={16} style={{ marginRight: '6px' }} /> Add Task
            </button>
          </div>

          {tasks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
              <ClipboardList size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p style={{ margin: 0 }}>No tasks assigned to this sub-project</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {tasks.map(task => {
                const taskStatus = getStatusStyle(task.status);
                return (
                  <div
                    key={task.id}
                    onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
                    style={{
                      padding: '16px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: '1px solid var(--border-color)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {task.status === 'completed' ? (
                        <CheckCircle size={20} color="#10B981" />
                      ) : (
                        <AlertCircle size={20} color="#F59E0B" />
                      )}
                      <div>
                        <div style={{ fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>{task.title}</div>
                        {task.deadline && (
                          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                            Due: {new Date(task.deadline).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: taskStatus.bg,
                        color: taskStatus.color
                      }}>
                        {taskStatus.label}
                      </span>
                      <ChevronRight size={16} color="var(--text-tertiary)" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Team Section */}
        <div className="card" style={{ padding: '24px' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={20} color="#7077FE" /> Team
          </h3>

          {subProject.team_heads && subProject.team_heads.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <UserCheck size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Team Heads ({subProject.team_heads.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {subProject.team_heads.map((th, idx) => (
                  <div
                    key={idx}
                    onClick={() => navigate(`/dashboard/profile/${th.id}`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      background: '#EEF2FF',
                      borderRadius: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    <UserAvatar user={th} size={36} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#4338CA' }}>{cleanName(th.full_name)}</div>
                      <div style={{ fontSize: '11px', color: '#6366F1', textTransform: 'capitalize' }}>{th.role?.replace('_', ' ')}</div>
                    </div>
                    <ChevronRight size={16} color="#6366F1" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {subProject.assigned_users && subProject.assigned_users.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Users size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Team Members ({subProject.assigned_users.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {subProject.assigned_users.map((m, idx) => (
                  <div
                    key={idx}
                    onClick={() => navigate(`/dashboard/profile/${m.id}`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      border: '1px solid var(--border-color)'
                    }}
                  >
                    <UserAvatar user={m} size={32} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>{cleanName(m.full_name)}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'capitalize' }}>{m.role?.replace('_', ' ')}</div>
                    </div>
                    <ChevronRight size={14} color="var(--text-tertiary)" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!subProject.team_heads || subProject.team_heads.length === 0) && (!subProject.assigned_users || subProject.assigned_users.length === 0) && (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>
              <Users size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
              <p style={{ margin: 0, fontSize: '13px' }}>No team assigned yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)' }}>Edit Sub-Project</h2>
              <button onClick={() => setShowEditModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdate}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Title *</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  required
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Description</label>
                <RichTextEditor
                  value={editForm.description}
                  onChange={(value) => setEditForm({ ...editForm, description: value })}
                  placeholder="Sub-project description..."
                  minHeight="120px"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  >
                    <option value="active">Active</option>
                    <option value="in_progress">In Progress</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Deadline</label>
                  <input
                    type="date"
                    value={editForm.deadline}
                    onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })}
                    style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowEditModal(false)} style={{ padding: '12px 24px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" className="gradient-btn" style={{ padding: '12px 24px' }}>Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notice Modal */}
      {showNoticeModal && (
        <div className="modal-overlay" onClick={() => setShowNoticeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Megaphone size={22} color="#F59E0B" /> {editingNotice ? 'Edit Notice' : 'Add Notice'}
              </h2>
              <button onClick={() => setShowNoticeModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleAddNotice}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Title *</label>
                <input
                  type="text"
                  value={noticeForm.title}
                  onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })}
                  required
                  style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Priority</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {['normal', 'important', 'urgent'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setNoticeForm({ ...noticeForm, priority: p })}
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        border: `2px solid ${noticeForm.priority === p ? (p === 'urgent' ? '#EF4444' : p === 'important' ? '#F59E0B' : '#7077FE') : 'var(--border-color)'}`,
                        borderRadius: '8px',
                        background: noticeForm.priority === p ? (p === 'urgent' ? '#FEE2E2' : p === 'important' ? '#FEF3C7' : '#EEF2FF') : 'transparent',
                        color: noticeForm.priority === p ? (p === 'urgent' ? '#DC2626' : p === 'important' ? '#D97706' : '#4338CA') : 'var(--text-secondary)',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        textTransform: 'capitalize'
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>Content *</label>
                <RichTextEditor
                  value={noticeForm.content}
                  onChange={(value) => setNoticeForm({ ...noticeForm, content: value })}
                  placeholder="Notice content..."
                  minHeight="150px"
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowNoticeModal(false)} style={{ padding: '12px 24px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" className="gradient-btn" style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Send size={16} /> {editingNotice ? 'Update' : 'Post Notice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubProjectView;
